// apps/admin/src/components/composer/zone-draggable.tsx
//
// P5 UI Polish W2 T1 — dnd-kit replacement for react-rnd's <Rnd>.
//
// Wraps a single zone with:
//   - useDraggable for the body (whole-zone drag)
//   - 8 resize handles, each a separate useDraggable with data.kind=
//     'zone-resize' so the DndContext can dispatch resize vs body
//     drag in handleDragEnd.

import type { CSSProperties } from 'react';
import { useDraggable } from '@dnd-kit/core';

export type ResizeHandle =
  | 'top'
  | 'right'
  | 'bottom'
  | 'left'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export interface ZoneDraggableProps {
  zoneId: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  selected: boolean;
  locked?: boolean;
  zIndex?: number;
  onSelect: (zoneId: string, shiftKey: boolean) => void;
  children: React.ReactNode;
}

export function ZoneDraggable(props: ZoneDraggableProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `zone-body-${props.zoneId}`,
    data: { kind: 'zone-body' as const, zoneId: props.zoneId },
    disabled: props.locked ?? false,
  });

  const style: CSSProperties = {
    position: 'absolute',
    left: props.position.x,
    top: props.position.y,
    width: props.size.width,
    height: props.size.height,
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    border: props.selected ? '2px solid #2563eb' : '1px solid #94a3b8',
    background: props.selected
      ? 'rgba(37, 99, 235, 0.08)'
      : 'rgba(148, 163, 184, 0.08)',
    boxSizing: 'border-box',
    cursor: props.locked ? 'default' : isDragging ? 'grabbing' : 'move',
    touchAction: 'none',
    zIndex: props.zIndex ?? 1,
  };

  return (
    <div
      ref={setNodeRef}
      data-testid={`layout-editor-zone-${props.zoneId}`}
      data-selected={props.selected}
      data-locked={!!props.locked}
      style={style}
      onMouseDown={(e) => {
        // Stop propagation so the canvas's "click on empty → deselect" handler
        // is not triggered when the zone itself is clicked.
        e.stopPropagation();
        props.onSelect(props.zoneId, e.shiftKey);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          props.onSelect(props.zoneId, e.shiftKey);
        }
      }}
      {...(props.locked ? {} : listeners)}
      {...attributes}
    >
      {props.children}
      {props.selected && !props.locked &&
        (['top-left', 'top-right', 'bottom-left', 'bottom-right', 'top', 'right', 'bottom', 'left'] as const).map((h) => (
          <ResizeHandleElement key={h} handle={h} zoneId={props.zoneId} />
        ))}
    </div>
  );
}

function ResizeHandleElement({ handle, zoneId }: { handle: ResizeHandle; zoneId: string }): JSX.Element {
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `zone-resize-${zoneId}-${handle}`,
    data: { kind: 'zone-resize' as const, zoneId, handle },
  });

  // Extract onPointerDown from listeners so we can wrap it with
  // stopPropagation — this prevents the body drag from also activating
  // when the user grabs a resize handle.
  const { onPointerDown: dndOnPointerDown, ...restListeners } = listeners ?? {};

  function handlePointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    // Prevent the body's useDraggable from receiving this pointer event.
    e.stopPropagation();
    if (typeof dndOnPointerDown === 'function') {
      dndOnPointerDown(e);
    }
  }

  return (
    <div
      ref={setNodeRef}
      data-testid={`resize-${handle}-${zoneId}`}
      style={{
        position: 'absolute',
        ...handlePosition(handle),
        background: '#2563eb',
        width: 8,
        height: 8,
        cursor: handleCursor(handle),
        touchAction: 'none',
        zIndex: 10,
      }}
      onPointerDown={handlePointerDown}
      {...restListeners}
      {...attributes}
    />
  );
}

function handlePosition(h: ResizeHandle): CSSProperties {
  const out: CSSProperties = {};
  if (h.includes('top')) out.top = -4;
  if (h.includes('bottom')) out.bottom = -4;
  if (h.includes('left')) out.left = -4;
  if (h.includes('right')) out.right = -4;
  if (h === 'top' || h === 'bottom') out.left = 'calc(50% - 4px)';
  if (h === 'left' || h === 'right') out.top = 'calc(50% - 4px)';
  return out;
}

function handleCursor(h: ResizeHandle): string {
  const map: Record<ResizeHandle, string> = {
    'top-left': 'nwse-resize',
    'top-right': 'nesw-resize',
    'bottom-left': 'nesw-resize',
    'bottom-right': 'nwse-resize',
    top: 'ns-resize',
    bottom: 'ns-resize',
    left: 'ew-resize',
    right: 'ew-resize',
  };
  return map[h];
}
